import { queryOptions } from '@tanstack/react-query';
import type {
  MerchantListParams,
  IntegrationTokenListParams,
} from '../lib/merchant-mappers';
import type {
  UpdateMerchantPayload,
  UpdateMerchantIntegrationPayload,
  UpdatePharmaIntegrationPayload,
} from '../model/schema';
import { merchantApi } from './merchant-api';

/**
 * Query-key factory + query-options for the Merchant domain. Mutations
 * invalidate through `merchantKeys` (never an inline array).
 */
export const merchantKeys = {
  all: ['merchants'] as const,
  lists: () => [...merchantKeys.all, 'list'] as const,
  list: (params: MerchantListParams) => [...merchantKeys.lists(), params] as const,
  details: () => [...merchantKeys.all, 'detail'] as const,
  detail: (id: string) => [...merchantKeys.details(), id] as const,
  integration: (id: string) => [...merchantKeys.all, 'integration', id] as const,
  integrationOptions: () => [...merchantKeys.all, 'integration-options'] as const,
  integrationCredentials: (id: string, provider: string) =>
    [...merchantKeys.all, 'integration-credentials', id, provider] as const,
  deliveryPricing: (id: string) => [...merchantKeys.all, 'delivery-pricing', id] as const,
  pharmaIntegration: (id: string) =>
    [...merchantKeys.all, 'pharma-integration', id] as const,
  employees: (merchantId: string, params?: { limit?: number; offset?: number }) =>
    [...merchantKeys.all, 'employees', merchantId, params ?? {}] as const,
  employee: (id: string) => [...merchantKeys.all, 'employee', id] as const,
  tokens: (params: IntegrationTokenListParams) =>
    [...merchantKeys.all, 'integration-tokens', params] as const,
  deliveryVendor: (merchantId: string) =>
    [...merchantKeys.all, 'delivery-vendor', merchantId] as const,
  /**
   * Just-saved delivery values awaiting by-external-id confirmation (see
   * `PendingDeliverySave`). A cache entry, not a query — never fetched.
   */
  pendingDeliverySave: (merchantId: string) =>
    [...merchantKeys.deliveryVendor(merchantId), 'pending-save'] as const,
};

export const merchantQueries = {
  list: (params: MerchantListParams) =>
    queryOptions({
      queryKey: merchantKeys.list(params),
      queryFn: ({ signal }) => merchantApi.list(params, { signal }),
      placeholderData: (prev) => prev,
    }),

  detail: (id: string, enabled = true) =>
    queryOptions({
      queryKey: merchantKeys.detail(id),
      queryFn: () => merchantApi.detail(id),
      enabled: enabled && Boolean(id),
    }),

  integration: (id: string, enabled = true) =>
    queryOptions({
      queryKey: merchantKeys.integration(id),
      queryFn: () => merchantApi.integration(id),
      enabled: enabled && Boolean(id),
      // Integration settings can change outside this form; always refresh
      // when the Eats integration block mounts instead of serving cached data.
      refetchOnMount: 'always',
    }),

  integrationOptions: (enabled = true) =>
    queryOptions({
      queryKey: merchantKeys.integrationOptions(),
      queryFn: () => merchantApi.integrationOptions(),
      enabled,
      staleTime: 5 * 60 * 1000,
    }),
  integrationCredentials: (id: string, provider: string, enabled = true) =>
    queryOptions({
      queryKey: merchantKeys.integrationCredentials(id, provider),
      queryFn: () => merchantApi.integrationCredentials(id, provider),
      enabled: enabled && Boolean(id) && Boolean(provider) && provider !== 'noor',
    }),
  deliveryPricing: (id: string, enabled = true) =>
    queryOptions({
      queryKey: merchantKeys.deliveryPricing(id),
      queryFn: () => merchantApi.deliveryPricing(id),
      enabled: enabled && Boolean(id),
      refetchOnMount: 'always',
    }),

  pharmaIntegration: (companyId: string, enabled = true) =>
    queryOptions({
      queryKey: merchantKeys.pharmaIntegration(companyId),
      queryFn: () => merchantApi.pharmaIntegration(companyId),
      enabled: enabled && Boolean(companyId),
      // A 404 means "never configured" — not worth retrying.
      retry: (failureCount, error) =>
        (error as { status?: number })?.status !== 404 && failureCount < 3,
    }),

  employees: (
    merchantId: string,
    enabled = true,
    params?: { limit?: number; offset?: number },
  ) =>
    queryOptions({
      queryKey: merchantKeys.employees(merchantId, params),
      queryFn: () =>
        merchantApi.employees({
          merchant_id: merchantId,
          limit: params?.limit ?? 20,
          offset: params?.offset ?? 0,
        }),
      enabled: enabled && Boolean(merchantId),
      placeholderData: (prev) => prev,
    }),

  integrationTokens: (params: IntegrationTokenListParams) =>
    queryOptions({
      queryKey: merchantKeys.tokens(params),
      queryFn: () => merchantApi.integrationTokens(params),
      placeholderData: (prev) => prev,
    }),

  deliveryVendor: (merchantId: string, enabled = true) =>
    queryOptions({
      queryKey: merchantKeys.deliveryVendor(merchantId),
      queryFn: () => merchantApi.deliveryVendor(merchantId),
      enabled: enabled && Boolean(merchantId),
      retry: false,
      staleTime: 0,
    }),

  employee: (id: string) =>
    queryOptions({
      queryKey: merchantKeys.employee(id),
      queryFn: () => merchantApi.employee(id),
      enabled: Boolean(id),
    }),
};

export type {
  UpdateMerchantPayload,
  UpdateMerchantIntegrationPayload,
  UpdatePharmaIntegrationPayload,
};
